/**
 * Calculate distance between two addresses using Google Maps Routes API (V1)
 * This avoids legacy API errors and uses the modern routing engine.
 * @param {Object} origin - { lat, lng }
 * @param {Object} destination - { lat, lng }
 * @returns {Promise<number>} - Distance in kilometers
 */
export async function calculateDistanceKm(origin, destination) {
  if (!origin || !destination) return 0;

  const lat1 = origin.lat || origin.latitude;
  const lng1 = origin.lng || origin.longitude;
  const lat2 = destination.lat || destination.latitude;
  const lng2 = destination.lng || destination.longitude;

  if (!lat1 || !lng1 || !lat2 || !lng2) {
    console.warn('[calculateDistanceKm] Missing coordinates');
    return 0;
  }

  try {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    const url = 'https://routes.googleapis.com/directions/v2:computeRoutes';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'routes.distanceMeters',
      },
      body: JSON.stringify({
        origin: {
          location: {
            latLng: {
              latitude: Number(lat1),
              longitude: Number(lng1),
            },
          },
        },
        destination: {
          location: {
            latLng: {
              latitude: Number(lat2),
              longitude: Number(lng2),
            },
          },
        },
        travelMode: 'DRIVE',
        routingPreference: 'TRAFFIC_UNAWARE',
        units: 'METRIC',
      }),
    });

    const data = await response.json();

    if (data.routes && data.routes.length > 0) {
      const distanceInMeters = data.routes[0].distanceMeters;
      const distanceKm = distanceInMeters / 1000;
      console.log(`[Routes API Distance] ${distanceKm} km`);
      return Math.round(distanceKm * 100) / 100;
    } else {
      console.warn('[Routes API] No routes found, falling back to Haversine');
      return calculateHaversineDistance(lat1, lng1, lat2, lng2);
    }
  } catch (err) {
    console.error('[Routes API] Fetch error:', err);
    return calculateHaversineDistance(lat1, lng1, lat2, lng2);
  }
}

/**
 * Haversine formula for fallback distance calculation
 */
function calculateHaversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance * 100) / 100;
}

/**
 * Parse weight from size string (e.g., "Small (Up to 2 kg)" -> 2)
 * @param {string} sizeString - Size description
 * @returns {number} - Weight in kg
 */
export function parseWeightFromSize(sizeString) {
  if (!sizeString) return 1;

  const lowerSize = sizeString.toLowerCase();

  // Try to extract number from format like "Small (Up to 2 kg)"
  const match = sizeString.match(/(\d+(?:\.\d+)?)\s*kg/i);
  if (match) {
    return parseFloat(match[1]);
  }

  // Fallback defaults
  if (lowerSize.includes('small')) return 2;
  if (lowerSize.includes('medium')) return 10;
  if (lowerSize.includes('large')) return 25;
  if (lowerSize.includes('extra')) return 50;

  return 5; // Default fallback
}

/**
 * Format price for display
 * @param {number} price
 * @returns {string}
 */
export function formatPrice(price) {
  if (price == null) return '0';
  return Math.round(price).toString();
}
